import {
  getNextColor,
  HSVtoRGB,
  wrap,
  getResolution,
  scaleByPixelRatio,
} from './utils.js'; // Changed generateColor to getNextColor
import {
  pointerPrototype,
  updatePointerDownData,
  updatePointerMoveData,
  updatePointerUpData,
} from './pointer.js';
import {
  getWebGLContext,
  hashCode,
  Material,
  Program,
  compileShader,
  blit as webglBlit,
  initBlit,
  createFBO,
  createDoubleFBO,
  resizeFBO,
  resizeDoubleFBO,
} from './gl_setup.js';
import {
  step as simulationStep,
  render as simulationRender,
  splatPointer as simulationSplatPointer,
  clickSplat as simulationClickSplat,
} from './simulation.js';

// Global state for the cursor instance and its management
let currentCursorInstance = null;
let globalCanvasContainer = null;
let globalCanvas = null;
let globalIsAnimationGloballyEnabled = true; // For the toggle switch
let globalMainUpdateFrame = null; // To control animation loop from toggle
let globalAnimationFrameId = null; // To cancel animation frame

// Event handler references for cleanup
let eventHandlers = {
  mousedown: null,
  firstMouseMove: null,
  mousemove: null,
  firstTouchStart: null,
  touchstart: null,
  touchmove: null,
  touchend: null,
};

function cleanupCurrentSplashCursor() {
  if (globalAnimationFrameId) {
    cancelAnimationFrame(globalAnimationFrameId);
    globalAnimationFrameId = null;
  }

  // Remove event listeners
  if (eventHandlers.mousedown) window.removeEventListener('mousedown', eventHandlers.mousedown);
  if (eventHandlers.firstMouseMove) document.body.removeEventListener('mousemove', eventHandlers.firstMouseMove);
  if (eventHandlers.mousemove) window.removeEventListener('mousemove', eventHandlers.mousemove);
  if (eventHandlers.firstTouchStart) document.body.removeEventListener('touchstart', eventHandlers.firstTouchStart);
  if (eventHandlers.touchstart) window.removeEventListener('touchstart', eventHandlers.touchstart);
  if (eventHandlers.touchmove) window.removeEventListener('touchmove', eventHandlers.touchmove);
  if (eventHandlers.touchend) window.removeEventListener('touchend', eventHandlers.touchend);

  // Reset handlers
  Object.keys(eventHandlers).forEach(key => eventHandlers[key] = null);

  if (currentCursorInstance && currentCursorInstance.gl) {
    const gl = currentCursorInstance.gl;
    // Attempt to clean up WebGL resources
    // This is tricky as direct 'delete' calls aren't always straightforward for all resources
    // and context loss is the most robust way.
    // For now, we'll rely on creating a new context which implicitly handles old one if canvas is reused.
    // If FBOs/textures/programs were stored on `currentCursorInstance`, we could iterate and delete them.
    // For simplicity in this refactor, we're not explicitly deleting individual WebGL resources here,
    // but a more robust solution might.
  }

  // The canvas and container are reused, so no need to remove them from DOM unless specified
  currentCursorInstance = null;
  globalMainUpdateFrame = null;
}

// Renamed to avoid conflict if old one is cached
function initializeNewSplashCursor(options = {}) {
  cleanupCurrentSplashCursor(); // Ensure any old instance is cleaned up first

  // Re-initialize or ensure canvas and container exist
  if (!globalCanvasContainer) {
    globalCanvasContainer = document.createElement('div');
    Object.assign(globalCanvasContainer.style, {
      position: 'fixed',
      top: 0,
      left: 0,
      pointerEvents: 'none',
      width: '100%',
      height: '100%',
    });
    document.body.appendChild(globalCanvasContainer);
  }

  if (!globalCanvas) {
    globalCanvas = document.createElement('canvas');
    globalCanvas.id = 'fluid'; // Ensure ID is set if SplashCursor relies on it
    Object.assign(globalCanvas.style, {
      width: '100vw',
      height: '100vh',
      display: 'block',
      background: 'transparent',
    });
    globalCanvasContainer.innerHTML = ''; // Clear previous canvas if any
    globalCanvasContainer.appendChild(globalCanvas);
  }

  // Ensure canvas is displayed if it was hidden by toggle
  if (globalCanvasContainer) globalCanvasContainer.style.display = globalIsAnimationGloballyEnabled ? '' : 'none';


  // SplashCursorCore is the main logic from the original SplashCursor function
  function SplashCursorCore(optionsParam) { // Parameter renamed for clarity
    // Destructure all needed properties directly from optionsParam.
    // These constants will be local to SplashCursorCore.
    const {
      SIM_RESOLUTION, DYE_RESOLUTION, CAPTURE_RESOLUTION, DENSITY_DISSIPATION,
      VELOCITY_DISSIPATION, PRESSURE, PRESSURE_ITERATIONS, CURL, SPLAT_RADIUS,
      SPLAT_FORCE, SHADING, DARK_MODE, COLOR_UPDATE_SPEED, BACK_COLOR,
      TRANSPARENT, SIZE, COLORS, Z_INDEX
    } = optionsParam; // Use optionsParam here

    // Apply Z_INDEX to the globalCanvasContainer using the destructured Z_INDEX
    Object.assign(globalCanvasContainer.style, { zIndex: Z_INDEX || 100 });

    if (!globalCanvas) return null;

    // Build the config object using these local constants.
    let config = {
      SIM_RESOLUTION: SIM_RESOLUTION,
      DYE_RESOLUTION: DYE_RESOLUTION,
      CAPTURE_RESOLUTION: CAPTURE_RESOLUTION,
      DENSITY_DISSIPATION: DENSITY_DISSIPATION,
      VELOCITY_DISSIPATION: VELOCITY_DISSIPATION,
      PRESSURE: PRESSURE,
      PRESSURE_ITERATIONS: PRESSURE_ITERATIONS,
      CURL: CURL,
      SPLAT_RADIUS: SPLAT_RADIUS, // This will be adjusted by SIZE logic next
      SPLAT_FORCE: SPLAT_FORCE,
      SHADING: SHADING,
      DARK_MODE: DARK_MODE,
      COLOR_UPDATE_SPEED: COLOR_UPDATE_SPEED,
      PAUSED: false,
      BACK_COLOR: BACK_COLOR,
      TRANSPARENT: TRANSPARENT,
      COLORS: COLORS,
      parsedColors: [],
      colorCycleIndex: 0,
    };

    // Adjust SPLAT_RADIUS based on SIZE
    if (SIZE === 'small') config.SPLAT_RADIUS = 0.1;
    else if (SIZE === 'large') config.SPLAT_RADIUS = 0.4;
    else config.SPLAT_RADIUS = 0.2;

    if (Array.isArray(config.COLORS)) {
      config.COLORS.forEach((hex) => {
        const result = /^#?([a-f\d]{1,2})([a-f\d]{1,2})([a-f\d]{1,2})$/i.exec(hex);
        if (result) {
          config.parsedColors.push({
            r: parseInt(result[1].length === 1 ? result[1] + result[1] : result[1], 16) / 255,
            g: parseInt(result[2].length === 1 ? result[2] + result[2] : result[2], 16) / 255,
            b: parseInt(result[3].length === 1 ? result[3] + result[3] : result[3], 16) / 255,
          });
        }
      });
    }
    // The duplicate COLORS.forEach loop was removed as it was redundant.
    // The mapping config.parsedColors = config.parsedColors.map(...) was also redundant
    // as the objects pushed already have the desired structure.

    let pointers = [new pointerPrototype()];
    const { gl, ext } = getWebGLContext(globalCanvas, config); // Use globalCanvas

    if (!gl) { // WebGL context might fail to initialize
        console.error("Failed to get WebGL context for SplashCursorCore");
        return null;
    }

    currentCursorInstance = { gl }; // Store gl for potential cleanup

    if (!ext.supportLinearFiltering) {
      config.DYE_RESOLUTION = 256;
      config.SHADING = false;
    }

    initBlit(gl);

    const baseVertexShader = compileShader(
      gl,
      gl.VERTEX_SHADER,
      `
        precision highp float;
        attribute vec2 aPosition;
        varying vec2 vUv;
        varying vec2 vL;
        varying vec2 vR;
        varying vec2 vT;
        varying vec2 vB;
        uniform vec2 texelSize;

        void main () {
            vUv = aPosition * 0.5 + 0.5;
            vL = vUv - vec2(texelSize.x, 0.0);
            vR = vUv + vec2(texelSize.x, 0.0);
            vT = vUv + vec2(0.0, texelSize.y);
            vB = vUv - vec2(0.0, texelSize.y);
            gl_Position = vec4(aPosition, 0.0, 1.0);
        }
      `
    );

    const copyShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        uniform sampler2D uTexture;

        void main () {
            gl_FragColor = texture2D(uTexture, vUv);
        }
      `
    );

    const clearShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        uniform sampler2D uTexture;
        uniform float value;

        void main () {
            gl_FragColor = value * texture2D(uTexture, vUv);
        }
      `
    );

    const displayShaderSource = `
      precision highp float;
      precision highp sampler2D;

      varying vec2 vUv;
      varying vec2 vL;
      varying vec2 vR;
      varying vec2 vT;
      varying vec2 vB;

      uniform sampler2D uTexture;
      uniform sampler2D uDithering;
      uniform vec2 ditherScale;
      uniform vec2 texelSize;

      vec3 linearToGamma(vec3 color) {
          color = max(color, vec3(0.0));
          return max(1.055 * pow(color, vec3(1.0 / 2.4)) - 0.055, vec3(0.0));
      }

      void main() {
          vec3 c = texture2D(uTexture, vUv).rgb;

          #ifdef SHADING
              vec3 lc = texture2D(uTexture, vL).rgb;
              vec3 rc = texture2D(uTexture, vR).rgb;
              vec3 tc = texture2D(uTexture, vT).rgb;
              vec3 bc = texture2D(uTexture, vB).rgb;

              float dx = length(rc) - length(lc);
              float dy = length(tc) - length(bc);

              vec3 n = normalize(vec3(dx, dy, length(texelSize)));
              vec3 l = vec3(0.0, 0.0, 1.0);

              float diffuse = clamp(dot(n, l) + 0.7, 0.7, 1.0);
              c *= diffuse;
          #endif


          #ifdef DARK
              float energy = length(c);
              float alpha = clamp(energy, 0.0, 1.0);
              if (alpha < 0.02) discard;

              gl_FragColor = vec4(c, alpha);
          #else
              float a = clamp(max(c.r, max(c.g, c.b)) * 1.2, 0.0, 1.0);
			        gl_FragColor = vec4(c, a);
          #endif
      }
    `;

    const splatShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        uniform sampler2D uTarget;
        uniform float aspectRatio;
        uniform vec3 color;
        uniform vec2 point;
        uniform float radius;

        void main () {
            vec2 p = vUv - point.xy;
            p.x *= aspectRatio;
            vec3 splat = exp(-dot(p, p) / radius) * color;
            vec3 base = texture2D(uTarget, vUv).xyz;
            gl_FragColor = vec4(base + splat, 1.0);
        }
      `
    );

    const advectionShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        uniform sampler2D uVelocity;
        uniform sampler2D uSource;
        uniform vec2 texelSize;
        uniform vec2 dyeTexelSize;
        uniform float dt;
        uniform float dissipation;

        vec4 bilerp (sampler2D sam, vec2 uv, vec2 tsize) {
            vec2 st = uv / tsize - 0.5;
            vec2 iuv = floor(st);
            vec2 fuv = fract(st);

            vec4 a = texture2D(sam, (iuv + vec2(0.5, 0.5)) * tsize);
            vec4 b = texture2D(sam, (iuv + vec2(1.5, 0.5)) * tsize);
            vec4 c = texture2D(sam, (iuv + vec2(0.5, 1.5)) * tsize);
            vec4 d = texture2D(sam, (iuv + vec2(1.5, 1.5)) * tsize);

            return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y);
        }

        void main () {
            #ifdef MANUAL_FILTERING
                vec2 coord = vUv - dt * bilerp(uVelocity, vUv, texelSize).xy * texelSize;
                vec4 result = bilerp(uSource, coord, dyeTexelSize);
            #else
                vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
                vec4 result = texture2D(uSource, coord);
            #endif
            float decay = 1.0 + dissipation * dt;
            gl_FragColor = result / decay;
        }
      `,
      ext.supportLinearFiltering ? null : ['MANUAL_FILTERING']
    );

    const divergenceShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uVelocity, vL).x;
            float R = texture2D(uVelocity, vR).x;
            float T = texture2D(uVelocity, vT).y;
            float B = texture2D(uVelocity, vB).y;

            vec2 C = texture2D(uVelocity, vUv).xy;
            if (vL.x < 0.0) { L = -C.x; }
            if (vR.x > 1.0) { R = -C.x; }
            if (vT.y > 1.0) { T = -C.y; }
            if (vB.y < 0.0) { B = -C.y; }

            float div = 0.5 * (R - L + T - B);
            gl_FragColor = vec4(div, 0.0, 0.0, 1.0);
        }
      `
    );

    const curlShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uVelocity, vL).y;
            float R = texture2D(uVelocity, vR).y;
            float T = texture2D(uVelocity, vT).x;
            float B = texture2D(uVelocity, vB).x;
            float vorticity = R - L - T + B;
            gl_FragColor = vec4(0.5 * vorticity, 0.0, 0.0, 1.0);
        }
      `
    );

    const vorticityShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision highp float;
        precision highp sampler2D;
        varying vec2 vUv;
        varying vec2 vL;
        varying vec2 vR;
        varying vec2 vT;
        varying vec2 vB;
        uniform sampler2D uVelocity;
        uniform sampler2D uCurl;
        uniform float curl;
        uniform float dt;

        void main () {
            float L = texture2D(uCurl, vL).x;
            float R = texture2D(uCurl, vR).x;
            float T = texture2D(uCurl, vT).x;
            float B = texture2D(uCurl, vB).x;
            float C = texture2D(uCurl, vUv).x;

            vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
            force /= length(force) + 0.0001;
            force *= curl * C;
            force.y *= -1.0;

            vec2 velocity = texture2D(uVelocity, vUv).xy;
            velocity += force * dt;
            velocity = min(max(velocity, -1000.0), 1000.0);
            gl_FragColor = vec4(velocity, 0.0, 1.0);
        }
      `
    );

    const pressureShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uPressure;
        uniform sampler2D uDivergence;

        void main () {
            float L = texture2D(uPressure, vL).x;
            float R = texture2D(uPressure, vR).x;
            float T = texture2D(uPressure, vT).x;
            float B = texture2D(uPressure, vB).x;
            float C = texture2D(uPressure, vUv).x;
            float divergence = texture2D(uDivergence, vUv).x;
            float pressure = (L + R + B + T - divergence) * 0.25;
            gl_FragColor = vec4(pressure, 0.0, 0.0, 1.0);
        }
      `
    );

    const gradientSubtractShader = compileShader(
      gl,
      gl.FRAGMENT_SHADER,
      `
        precision mediump float;
        precision mediump sampler2D;
        varying highp vec2 vUv;
        varying highp vec2 vL;
        varying highp vec2 vR;
        varying highp vec2 vT;
        varying highp vec2 vB;
        uniform sampler2D uPressure;
        uniform sampler2D uVelocity;

        void main () {
            float L = texture2D(uPressure, vL).x;
            float R = texture2D(uPressure, vR).x;
            float T = texture2D(uPressure, vT).x;
            float B = texture2D(uPressure, vB).x;
            vec2 velocity = texture2D(uVelocity, vUv).xy;
            velocity.xy -= vec2(R - L, T - B);
            gl_FragColor = vec4(velocity, 0.0, 1.0);
        }
      `
    );

    // blit function moved to gl_setup.js and renamed to webglBlit

    let dye, velocity, divergence, curl, pressure;

    const copyProgram = new Program(gl, baseVertexShader, copyShader);
    const clearProgram = new Program(gl, baseVertexShader, clearShader);
    const splatProgram = new Program(gl, baseVertexShader, splatShader);
    const advectionProgram = new Program(gl, baseVertexShader, advectionShader);
    const divergenceProgram = new Program(
      gl,
      baseVertexShader,
      divergenceShader
    );
    const curlProgram = new Program(gl, baseVertexShader, curlShader);
    const vorticityProgram = new Program(gl, baseVertexShader, vorticityShader);
    const pressureProgram = new Program(gl, baseVertexShader, pressureShader);
    const gradienSubtractProgram = new Program(
      gl,
      baseVertexShader,
      gradientSubtractShader
    );
    const displayMaterial = new Material(
      gl,
      baseVertexShader,
      displayShaderSource
    );

    function initFramebuffers() {
      let simRes = getResolution(gl, config.SIM_RESOLUTION);
      let dyeRes = getResolution(gl, config.DYE_RESOLUTION);
      const texType = ext.halfFloatTexType;
      const rgba = ext.formatRGBA;
      const rg = ext.formatRG;
      const r = ext.formatR;
      const filtering = ext.supportLinearFiltering ? gl.LINEAR : gl.NEAREST;
      gl.disable(gl.BLEND);

      if (!dye)
        dye = createDoubleFBO(
          // Use imported function
          gl,
          dyeRes.width,
          dyeRes.height,
          rgba.internalFormat,
          rgba.format,
          texType,
          filtering
        );
      else
        dye = resizeDoubleFBO(
          // Use imported function
          gl,
          dye,
          dyeRes.width,
          dyeRes.height,
          rgba.internalFormat,
          rgba.format,
          texType,
          filtering,
          copyProgram,
          webglBlit
        );

      if (!velocity)
        velocity = createDoubleFBO(
          // Use imported function
          gl,
          simRes.width,
          simRes.height,
          rg.internalFormat,
          rg.format,
          texType,
          filtering
        );
      else
        velocity = resizeDoubleFBO(
          // Use imported function
          gl,
          velocity,
          simRes.width,
          simRes.height,
          rg.internalFormat,
          rg.format,
          texType,
          filtering,
          copyProgram,
          webglBlit
        );

      divergence = createFBO(
        // Use imported function
        gl,
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST
      );
      curl = createFBO(
        // Use imported function
        gl,
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST
      );
      pressure = createDoubleFBO(
        // Use imported function
        gl,
        simRes.width,
        simRes.height,
        r.internalFormat,
        r.format,
        texType,
        gl.NEAREST
      );
    }

    // createFBO, createDoubleFBO, resizeFBO, resizeDoubleFBO moved to gl_setup.js
    // splatPointer, clickSplat, splat, correctRadius will be moved to simulation.js

    // updateKeywords, initFramebuffers, lastUpdateTime, colorUpdateTimer, updateFrame, calcDeltaTime,
    // resizeCanvas, updateColors, applyInputs are part of the simulation or main loop, keep in main.js for now.

    // Event listeners also remain in main.js as they tie UI to the simulation logic.

    function updateKeywords() {
      let displayKeywords = [];
      if (config.SHADING) displayKeywords.push('SHADING');
      if (config.DARK_MODE) displayKeywords.push('DARK');
      displayMaterial.setKeywords(displayKeywords);
    }

    updateKeywords();
    initFramebuffers();
    let lastUpdateTime = Date.now();
    let colorUpdateTimer = 0.0;

    function updateFrame_internal() { // Renamed to avoid conflict if any outer scope uses this name
      if (!globalIsAnimationGloballyEnabled) { // Use global toggle state
        if (globalAnimationFrameId) {
          cancelAnimationFrame(globalAnimationFrameId);
          globalAnimationFrameId = null;
        }
        return;
      }
      const dt = calcDeltaTime();
      if (resizeGlobalCanvas()) initFramebuffers(); // Use global canvas
      updateColors(dt);
      applyInputs();
      simulationStep(
        gl, config, ext, velocity, dye, curl, pressure, divergence,
        curlProgram, vorticityProgram, divergenceProgram, clearProgram,
        pressureProgram, gradienSubtractProgram, advectionProgram, webglBlit, dt
      );
      simulationRender(gl, null, displayMaterial, dye, webglBlit, config);
      globalAnimationFrameId = requestAnimationFrame(updateFrame_internal);
    }
    globalMainUpdateFrame = updateFrame_internal;

    function calcDeltaTime() {
      let now = Date.now();
      let dt = (now - lastUpdateTime) / 1000;
      dt = Math.min(dt, 0.016666);
      lastUpdateTime = now;
      return dt;
    }

    function resizeGlobalCanvas() { // Renamed
      let width = scaleByPixelRatio(globalCanvas.clientWidth);
      let height = scaleByPixelRatio(globalCanvas.clientHeight);
      if (globalCanvas.width !== width || globalCanvas.height !== height) {
        globalCanvas.width = width;
        globalCanvas.height = height;
        return true;
      }
      return false;
    }

    function updateColors(dt) { // dt is delta time
      colorUpdateTimer += dt * config.COLOR_UPDATE_SPEED; // Use local config
      if (colorUpdateTimer >= 1) {
        colorUpdateTimer = wrap(colorUpdateTimer, 0, 1);
        pointers.forEach((p) => {
          p.color = getNextColor(config);
        });
      }
    }

    function applyInputs() {
      pointers.forEach((p) => {
        if (p.moved) {
          p.moved = false;
          simulationSplatPointer(gl, p, config, globalCanvas, velocity, dye, splatProgram, webglBlit);
        }
      });
    }

    // Define event handlers
    eventHandlers.mousedown = (e) => {
      let pointer = pointers[0];
      let posX = scaleByPixelRatio(e.clientX);
      let posY = scaleByPixelRatio(e.clientY);
      updatePointerDownData(pointer, -1, posX, posY, globalCanvas, () => getNextColor(config));
      simulationClickSplat(gl, pointer, globalCanvas, velocity, dye, splatProgram, config, webglBlit, () => getNextColor(config));
    };

    eventHandlers.firstMouseMove = (e) => {
      let pointer = pointers[0];
      let posX = scaleByPixelRatio(e.clientX);
      let posY = scaleByPixelRatio(e.clientY);
      if (globalIsAnimationGloballyEnabled && globalMainUpdateFrame && !globalAnimationFrameId) {
        globalMainUpdateFrame();
      }
      updatePointerMoveData(pointer, posX, posY, globalCanvas, getNextColor(config));
      document.body.removeEventListener('mousemove', eventHandlers.firstMouseMove);
      // Add the regular mousemove listener now
      window.addEventListener('mousemove', eventHandlers.mousemove);
    };

    eventHandlers.mousemove = (e) => {
        let pointer = pointers[0];
        let posX = scaleByPixelRatio(e.clientX);
        let posY = scaleByPixelRatio(e.clientY);
        updatePointerMoveData(pointer, posX, posY, globalCanvas, pointer.color);
    };

    eventHandlers.firstTouchStart = (e) => {
      const touches = e.targetTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        let posX = scaleByPixelRatio(touches[i].clientX);
        let posY = scaleByPixelRatio(touches[i].clientY);
        if (globalIsAnimationGloballyEnabled && globalMainUpdateFrame && !globalAnimationFrameId) {
          globalMainUpdateFrame();
        }
        updatePointerDownData(pointer, touches[i].identifier, posX, posY, globalCanvas, () => getNextColor(config));
      }
      document.body.removeEventListener('touchstart', eventHandlers.firstTouchStart);
      // Add the regular touchstart listener now
      window.addEventListener('touchstart', eventHandlers.touchstart);
    };

    eventHandlers.touchstart = (e) => {
      const touches = e.targetTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        let posX = scaleByPixelRatio(touches[i].clientX);
        let posY = scaleByPixelRatio(touches[i].clientY);
        updatePointerDownData(pointer, touches[i].identifier, posX, posY, globalCanvas, () => getNextColor(config));
      }
    };

    eventHandlers.touchmove = (e) => {
      const touches = e.targetTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        let posX = scaleByPixelRatio(touches[i].clientX);
        let posY = scaleByPixelRatio(touches[i].clientY);
        updatePointerMoveData(pointer, posX, posY, globalCanvas, pointer.color);
      }
    };

    eventHandlers.touchend = (e) => {
      const touches = e.changedTouches;
      let pointer = pointers[0];
      for (let i = 0; i < touches.length; i++) {
        updatePointerUpData(pointer);
      }
    };

    // Add event listeners using the stored handlers
    window.addEventListener('mousedown', eventHandlers.mousedown);
    document.body.addEventListener('mousemove', eventHandlers.firstMouseMove); // Initial one
    // Note: 'mousemove' for window is added after first move
    document.body.addEventListener('touchstart', eventHandlers.firstTouchStart); // Initial one
    // Note: 'touchstart' for window is added after first touch
    window.addEventListener('touchmove', eventHandlers.touchmove, false);
    window.addEventListener('touchend', eventHandlers.touchend);

    if (globalIsAnimationGloballyEnabled && globalMainUpdateFrame) {
      globalMainUpdateFrame();
    }
    return { gl }; // Return the context or instance details if needed for cleanup
  } // End of SplashCursorCore

  currentCursorInstance = SplashCursorCore(options);
} // End of initializeNewSplashCursor


// Global function to be called from admin settings
window.grSplashCursorUpdatePreview = function(newOptions) {
  initializeNewSplashCursor(newOptions);
};

// Initial setup on DOMContentLoaded
window.addEventListener('DOMContentLoaded', () => {
  // Initialize canvas and container once
  if (!globalCanvasContainer) {
      globalCanvasContainer = document.createElement('div');
      Object.assign(globalCanvasContainer.style, {
          position: 'fixed', top: 0, left: 0, pointerEvents: 'none',
          width: '100%', height: '100%',
      });
      document.body.appendChild(globalCanvasContainer);
  }
  if (!globalCanvas) {
      globalCanvas = document.createElement('canvas');
      globalCanvas.id = 'fluid';
      Object.assign(globalCanvas.style, {
          width: '100vw', height: '100vh', display: 'block', background: 'transparent',
      });
      globalCanvasContainer.appendChild(globalCanvas);
  }

  initializeNewSplashCursor(window.gr_splash_cursor_OPTIONS || {});

  // Toggle animation logic (simplified and uses global state)
  const toggleAnimation = (showAnimation) => {
    globalIsAnimationGloballyEnabled = showAnimation;
    if (showAnimation) {
      if (globalCanvasContainer) globalCanvasContainer.style.display = '';
      if (globalMainUpdateFrame && !globalAnimationFrameId) { // Check global animation frame ID
        globalMainUpdateFrame();
      }
    } else {
      if (globalCanvasContainer) globalCanvasContainer.style.display = 'none';
      if (globalAnimationFrameId) { // Check global animation frame ID
        cancelAnimationFrame(globalAnimationFrameId);
        globalAnimationFrameId = null;
      }
    }
  };

  // User toggle switch logic (if ALLOW_USER_TOGGLE is true)
  const initialOptions = window.gr_splash_cursor_OPTIONS || {};
  if (initialOptions.ALLOW_USER_TOGGLE) {
    const toggleCheckbox = document.querySelector('.gr-splash-cursor-user-toggle-trigger');
    const LOCAL_STORAGE_KEY = 'gr_splash_cursor_animation_enabled';
    if (toggleCheckbox) {
      let savedPreference = localStorage.getItem(LOCAL_STORAGE_KEY);
      let shouldAnimate = savedPreference !== null ? (savedPreference === 'true') : true;

      toggleAnimation(shouldAnimate); // This now uses the global toggle function
      toggleCheckbox.checked = shouldAnimate;

      toggleCheckbox.addEventListener('click', () => {
        const newState = toggleCheckbox.checked;
        toggleAnimation(newState);
        localStorage.setItem(LOCAL_STORAGE_KEY, newState.toString());
      });
    }
  }

  // Iframe pointer events handling
  const { ENABLE_IFRAMES, INCLUDE_IFRAMES_CLASS, EXCLUDE_IFRAMES_CLASS, IFRAME_RECURSIVE_SEARCH } = window.gr_splash_cursor_OPTIONS || {};

  if (ENABLE_IFRAMES) {
    function waitForIframes(callback, maxAttempts = 20) {
      let attempts = 0;
      const check = () => {
        const includeClasses = INCLUDE_IFRAMES_CLASS ? INCLUDE_IFRAMES_CLASS.split(',').map(s => s.trim()).filter(s => s) : [];
        const excludeClasses = EXCLUDE_IFRAMES_CLASS ? EXCLUDE_IFRAMES_CLASS.split(',').map(s => s.trim()).filter(s => s) : [];

        let finalFilteredIframes = [];

        if (includeClasses.length > 0) {
          const targetIframesSet = new Set();
          const allIframesNodeList = document.querySelectorAll('iframe'); // Get all iframes for direct check if not recursive
          allIframesNodeList.forEach(iframe => { // Iterate all iframes to check directly or via parent
            let included = false;
            for (const cls of includeClasses) {
              if (IFRAME_RECURSIVE_SEARCH) {
                if (iframe.matches('.' + cls.trim()) || iframe.closest('.' + cls.trim())) {
                  included = true;
                  break;
                }
              } else {
                if (iframe.classList.contains(cls.trim())) {
                  included = true;
                  break;
                }
              }
            }
            if (included) {
              targetIframesSet.add(iframe);
            }
          });
          // If recursive search is on, also check elements with class for iframe children
          if (IFRAME_RECURSIVE_SEARCH) {
            includeClasses.forEach(cls => {
                const parentElements = document.querySelectorAll('.' + cls.trim());
                parentElements.forEach(parentEl => {
                    if (parentEl.tagName === 'IFRAME' && parentEl.classList.contains(cls.trim())) { // If parentEl is an iframe itself
                        targetIframesSet.add(parentEl);
                    }
                    parentEl.querySelectorAll('iframe').forEach(iframe => targetIframesSet.add(iframe));
                });
            });
          }
          finalFilteredIframes = Array.from(targetIframesSet);

        } else if (excludeClasses.length > 0) {
          const allIframesNodeList = document.querySelectorAll('iframe');
          const targetIframesSet = new Set();

          allIframesNodeList.forEach(iframe => {
            let isExcluded = false;
            for (const cls of excludeClasses) {
              if (IFRAME_RECURSIVE_SEARCH) {
                if (iframe.matches('.' + cls.trim()) || iframe.closest('.' + cls.trim())) {
                  isExcluded = true;
                  break;
                }
              } else {
                if (iframe.classList.contains(cls.trim())) {
                  isExcluded = true;
                  break;
                }
              }
            }
            if (!isExcluded) {
              targetIframesSet.add(iframe);
            }
          });
          finalFilteredIframes = Array.from(targetIframesSet);
        } else {
          // No include or exclude classes, target all iframes currently in DOM
          finalFilteredIframes = Array.from(document.querySelectorAll('iframe'));
        }

        // Proceed with callback if iframes are found OR max attempts reached
        // This allows handling of pages with no iframes without hanging.
        if (finalFilteredIframes.length > 0 || attempts >= maxAttempts) {
          callback(finalFilteredIframes);
        } else {
          attempts++;
          setTimeout(check, 200);
        }
      };
      check();
    }

    waitForIframes((iframes) => {
      if (iframes.length === 0) return;

      let moveTimeout;

      const disableIframePointerEvents = () => {
        iframes.forEach(iframe => iframe.style.pointerEvents = 'none');
      };

      const enableIframePointerEvents = () => {
        iframes.forEach(iframe => iframe.style.pointerEvents = 'auto');
      };

      // Disable pointer events initially on mousemove for a brief period
      document.addEventListener('mousemove', () => {
        clearTimeout(moveTimeout);
        disableIframePointerEvents();
        moveTimeout = setTimeout(enableIframePointerEvents, 50); // Re-enable after 50ms
      });

      // Ensure pointer events are enabled on mousedown (e.g., to allow clicking links/buttons in iframes)
      document.addEventListener('mousedown', enableIframePointerEvents);

      // Initially disable pointer events on iframes until the first mousemove or mousedown
      disableIframePointerEvents();
    });
  }
});
